import { notifications } from "@mantine/notifications";
import type { QueryClient } from "@tanstack/react-query";

import { invalidateTaskViews } from "./invalidateTaskViews";
import { requestErrorText } from "./requestErrorText";
import type { TFunction } from "./requestErrorText";
import { ApiError } from "../../api/apiError";

const HTTP_CONFLICT = 409;
const NOT_AWAITING_PLAN_APPROVAL = "not_awaiting_plan_approval";

// Коды отказов одобрения/отклонения плана (404/409/422); данных случая у
// них нет — клиент перечитывает задачу (`not_awaiting_plan_approval`) либо
// показывает текст по коду. `plan_not_found` сюда не входит: это штатное
// «плана нет» GET /plan, `fetchTaskPlan` отдаёт `null`.
export const PLAN_REFUSAL_CODES = [
  "task_not_found",
  NOT_AWAITING_PLAN_APPROVAL,
  "no_pending_plan",
  "no_repository",
  "no_tracker_connection",
  "dependency_cycle",
] as const;

export type PlanRefusalCode = (typeof PLAN_REFUSAL_CODES)[number];

export function isPlanRefusalCode(code: string): code is PlanRefusalCode {
  return (PLAN_REFUSAL_CODES as readonly string[]).includes(code);
}

// Текст отказа по `code`; незнакомый или отсутствующий `code` — по статусу.
export function planRefusalText(error: Error, t: TFunction): string {
  if (
    error instanceof ApiError &&
    error.code !== null &&
    isPlanRefusalCode(error.code)
  ) {
    return t(`task.planRefusal.${error.code}`);
  }
  return requestErrorText(error, t);
}

// 409 `not_awaiting_plan_approval`: состояние задачи изменилось, её надо
// перечитать.
export function isPlanStateConflict(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === HTTP_CONFLICT &&
    error.code === NOT_AWAITING_PLAN_APPROVAL
  );
}

// Статус, лог переходов, план, список и активная задача очереди могут все
// измениться: набор `invalidateTaskViews` плюс план.
export function invalidatePlanViews(
  queryClient: QueryClient,
  taskId: string,
): void {
  invalidateTaskViews(queryClient, taskId);
  void queryClient.invalidateQueries({ queryKey: ["task-plan", taskId] });
}

// Отказ одобрения/отклонения плана на уровне хука мутации: колбэки
// `useMutation` срабатывают и после размонтирования баннера (статус задачи
// сменился, баннер пропал до ответа). На 409 `not_awaiting_plan_approval`
// перечитываем задачу и план и показываем уведомление — текст в баннере
// пропал бы вместе с ним. Прочие отказы показывает Alert баннера.
export function handlePlanDecisionError(
  queryClient: QueryClient,
  taskId: string,
  error: unknown,
  t: TFunction,
): void {
  if (isPlanStateConflict(error)) {
    invalidatePlanViews(queryClient, taskId);
    notifications.show({
      message: t(`task.planRefusal.${NOT_AWAITING_PLAN_APPROVAL}`),
      color: "yellow",
    });
  }
}
